import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/layout/Layout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Documents } from "./pages/Documents";
import FDVRouter from "./pages/sales/FDVRouter";
import { MySales } from "./pages/MySales";
import { Commissions } from "./pages/Commissions";
import { MyInvoices } from "./pages/MyInvoices";
import { Invoices } from "./pages/Invoices";
import { LeadsHot } from "./pages/LeadsHot";
import { LeadProfile } from "./pages/LeadProfile";
import { Animation } from "./pages/Animation";
import { News } from "./pages/News";
import { Support } from "./pages/Support";
import { Messages } from "./pages/Messages";
import { Settings } from "./pages/Settings";
import { DocuClient } from "./pages/DocuClient";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminSales } from "./pages/admin/AdminSales";
import { AdminPayments } from "./pages/admin/AdminPayments";
import { AdminDocuments } from "./pages/admin/AdminDocuments";
import { AdminAnalytics } from "./pages/admin/AdminAnalytics";
import { AdminAnimation } from "./pages/admin/AdminAnimation";
import { AdminRHPayments } from "./pages/admin/AdminRHPayments";
import { AdminFDC } from "./pages/admin/AdminFDC";
import { AdminDocuClient } from "./pages/admin/AdminDocuClient";
import { AdminAttestations } from "./pages/admin/AdminAttestations";
import { AdminAgencies } from "./pages/admin/AdminAgencies";
import { AdminAuditLogs } from "./pages/admin/AdminAuditLogs";
import { AdminFDVBuilder } from "./pages/admin/AdminFDVBuilder";
import { SystemHealth } from "./pages/admin/SystemHealth";
import { TCSU } from "./pages/admin/TCSU";
// RH Pages
import { RHDashboard } from "./pages/rh/RHDashboard";
import { RHCommercials } from "./pages/rh/RHCommercials";
import { FDCForm } from "./pages/rh/FDCForm";
import { RHDocuAC } from "./pages/rh/RHDocuAC";
import { RHMesFactures } from "./pages/rh/RHMesFactures";
// Shared Pages
import { Attestations } from "./pages/Attestations";
import { Loader2 } from "lucide-react";

// Protected route wrapper
const ProtectedRoute = ({ children, adminOnly = false, rhOnly = false }) => {
  const { user, loading, isAdmin, isRH } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (rhOnly && !isRH && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public route - redirect to dashboard if logged in
const PublicRoute = ({ children }) => {
  const { user, loading, isRH } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (user) {
    // Redirect RH to their dashboard
    if (isRH) {
      return <Navigate to="/rh/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
          
          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="documents" element={<Documents />} />
            <Route path="declare-sale" element={<FDVRouter />} />
            <Route path="my-sales" element={<MySales />} />
            <Route path="commissions" element={<Commissions />} />
            <Route path="my-invoices" element={<MyInvoices />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="docu-client" element={<DocuClient />} />
            <Route path="leads-hot" element={<LeadsHot />} />
            <Route path="leads-hot/:prospectId" element={<LeadProfile />} />
            <Route path="animation" element={<Animation />} />
            <Route path="news" element={<News />} />
            <Route path="support" element={<Support />} />
            <Route path="messages" element={<Messages />} />
            <Route path="settings" element={<Settings />} />
            <Route path="attestations" element={<Attestations />} />
            
            {/* RH routes */}
            <Route path="rh/dashboard" element={<ProtectedRoute rhOnly><RHDashboard /></ProtectedRoute>} />
            <Route path="rh/commercials" element={<ProtectedRoute rhOnly><RHCommercials /></ProtectedRoute>} />
            <Route path="rh/fdc" element={<ProtectedRoute rhOnly><FDCForm /></ProtectedRoute>} />
            <Route path="rh/mes-factures" element={<ProtectedRoute rhOnly><RHMesFactures /></ProtectedRoute>} />
            <Route path="rh/docu-ac" element={<ProtectedRoute rhOnly><RHDocuAC /></ProtectedRoute>} />
            
            {/* Admin routes */}
            <Route path="admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
            <Route path="admin/fdc" element={<ProtectedRoute adminOnly><AdminFDC /></ProtectedRoute>} />
            <Route path="admin/sales" element={<ProtectedRoute adminOnly><AdminSales /></ProtectedRoute>} />
            <Route path="admin/payments" element={<ProtectedRoute adminOnly><AdminPayments /></ProtectedRoute>} />
            <Route path="admin/rh-payments" element={<ProtectedRoute adminOnly><AdminRHPayments /></ProtectedRoute>} />
            <Route path="admin/documents" element={<ProtectedRoute adminOnly><AdminDocuments /></ProtectedRoute>} />
            <Route path="admin/docu-client" element={<ProtectedRoute adminOnly><AdminDocuClient /></ProtectedRoute>} />
            <Route path="admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>} />
            <Route path="admin/animation" element={<ProtectedRoute adminOnly><AdminAnimation /></ProtectedRoute>} />
            <Route path="admin/attestations" element={<ProtectedRoute adminOnly><AdminAttestations /></ProtectedRoute>} />
            <Route path="admin/agencies" element={<ProtectedRoute adminOnly><AdminAgencies /></ProtectedRoute>} />
            <Route path="admin/audit-logs" element={<ProtectedRoute adminOnly><AdminAuditLogs /></ProtectedRoute>} />
            <Route path="admin/fdv-builder" element={<ProtectedRoute adminOnly><AdminFDVBuilder /></ProtectedRoute>} />
            <Route path="admin/system-health" element={<ProtectedRoute adminOnly><SystemHealth /></ProtectedRoute>} />
            <Route path="admin/tcsu" element={<ProtectedRoute adminOnly><TCSU /></ProtectedRoute>} />
          </Route>
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
